import { create } from "zustand"

import {
  type SortingOrder,
  type WarehouseShelving,
  WarehouseShelvingListDocument,
  type WarehouseShelvingListQuery,
  type WarehouseShelvingSortField,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"
import { withDevtools } from "@/utils/auth/withDevtools"

export type WarehouseShelvingSearchFilters = SearchFilters & {
  field?: WarehouseShelvingSortField
  order?: SortingOrder
}

const deduplicateWarehouseShelving = (
  newData: WarehouseShelving[],
  existingData: WarehouseShelving[]
): WarehouseShelving[] => {
  const map = new Map(existingData.map((existing) => [existing.warehouseShelvingId, existing]))
  for (const newItem of newData) {
    map.set(newItem.warehouseShelvingId, newItem) // newData replaces existing
  }
  return Array.from(map.values())
}

export const useWarehouseShelvingSearchStore = create(
  withDevtools(
    createSearchStore<WarehouseShelving, WarehouseShelvingSearchFilters, WarehouseShelvingListQuery>({
      documentNode: WarehouseShelvingListDocument,
      deduplicateData: deduplicateWarehouseShelving,
      getQueryInput: (filters: WarehouseShelvingSearchFilters, offset: number, limit: number) => {
        const { field, order, ...otherFilters } = filters
        return {
          filter: {
            ...otherFilters,
          },
          offset,
          limit,
          sort: { field, order },
        }
      },
      getQueryVariables: (queryInput) => ({ input: queryInput }),
      extractDataFromResponse: (data) => ({
        __typename: data.warehouseShelving.list.__typename,
        data:
          data.warehouseShelving.list.__typename === "WarehouseShelvingListSuccess"
            ? (data.warehouseShelving.list.warehouseShelvings as WarehouseShelving[])
            : [],
        error:
          data.warehouseShelving.list.__typename === "WarehouseShelvingListFailure"
            ? data.warehouseShelving.list.error
            : undefined,
      }),
    }),
    "WarehouseShelvingSearchStore"
  )
)
